import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { defer, formatDateTime } from '/imports/utils';
import { Icon } from '/imports/ui/Icon';
import { css } from '/imports/ui/css';
import { useFind, useFindOne, useSubscribe } from '/imports/hooks';
import { Publications } from '/imports/publications';
import { Notifications, Notification, markNotificationsRead } from '/imports/api/notifications';
import { ScrollDiv } from '/imports/ui/ScrollDiv';
import { toastOnError } from '/imports/ui/utils';
import { NotificationsCount } from '/imports/collections';

const NotificationDrawerStyled = styled.div`
  position: relative;
  display: flex;
  .toggler {
    display: flex;
    cursor: pointer;
    padding: 0 1rem;
    box-sizing: border-box;
    position: relative;
    ${css.mobileMedia} {
      min-width: 0;
      padding: 0 1rem;
    }
    .icon {
      svg {
        /* animation: pulse 1.5s ease-in-out infinite; */
      }
    }
    .badge {
      position: absolute;
      top: 0;
      right: 0;
      background-color: ${css.primaryColor};
      color: ${css.white};
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 0.75rem;
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-size: 0.8rem;
    }
  }
  .dropdown {
    position: absolute;
    right: 0;
    top: 100%;
    background: #fff;
    border: 2px solid ${css.black};
    min-width: 20rem;
    max-height: calc(100vh - ${css.appHeaderHeight} - 4px);
    height: auto;
    overflow: auto;
    display: none;
    z-index: 1;
    &.open {
      display: block;
    }
  }
  @keyframes pulse {
    0%, 100% { 
      transform: scale(1);
      fill: ${css.primaryColor};
    }
    50% { 
      transform: scale(1.2);
      fill: ${css.red1};
    }
  }
`;

/** Header bell with unread badge and a paginated dropdown of notifications. */
export const NotificationDrawer = () => {
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(10);
  useSubscribe(Publications.notificationsCount);
  const loading = useTracker(() => {
    const handle = Meteor.subscribe(Publications.notifications, { limit });
    return !handle.ready();
  }, [limit]);
  const notifications = useFind(() => Notifications.find({}, { sort: { createdAt: -1 } }), [loading]);
  useEffect(() => {
    if (open) {
      document.body.addEventListener('click', () => {
        defer(() => {setOpen(false); markRead();});
      }, { once: true });
    }
  }, [open]);
  const numUnread = useFindOne(() => NotificationsCount.find('_id'), [open]);
  const markRead = toastOnError(async () => {
    await markNotificationsRead.call();
  });
  return (
    <NotificationDrawerStyled className="user-dropdown">
      <div className="toggler" onClick={(e) => { e.stopPropagation(); setOpen(!open); open && markRead(); }}>
        <Icon icon="notifications" fill={css.primaryColor} />
        {numUnread?.count && <span className="badge">{numUnread.count}</span> || null}
      </div>
      <ScrollDiv className={`dropdown${open ? ' open' : ''}`} onScrollBottom={() => setLimit(limit + 10)}>
        {
          notifications.map((n) => <NotificationItem key={n._id} notification={n} />)
        }
      </ScrollDiv>
    </NotificationDrawerStyled>
  );
};

const NotificationItemStyled = styled(Link)`
  padding: 1rem;
  cursor: pointer;
  border-bottom: 1px solid black;
  display: block;
  text-decoration: none;
  position: relative;
  &.unread {
    background-color: #d7ffd3;
  }
  .date {
    font-size: 0.75rem;
    color: ${css.grey5};
  }
  .title {
    margin: 0;
    font-size: 1rem;
    font-weight: bold;
    color: ${css.grey4};
  }
  .body {
    font-size: 0.85rem;
    color: black;
  }
  &:hover {
    background: ${css.grey8};
  }
`;

interface NotificationItemProps {
  notification: Notification
}

/** Single notification row linking to its target. */
const NotificationItem = (props: NotificationItemProps) => {
  const { notification } = props;
  return (
    <NotificationItemStyled className={`item ${notification.isRead ? '' : ' unread'}`} to={notification.link || '#'}>
      <div className="date">{notification.createdAt && formatDateTime(notification.createdAt) || null}</div>
      <div className="title">{notification.title}</div>
      <div className="body">
        {notification.body}
      </div>
    </NotificationItemStyled>
  );
};
